import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { UserIdService } from '@spartacus/core'
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import { Observable, of } from 'rxjs'
import { catchError, map, retry } from 'rxjs/operators'
import { CustomPaginationType } from '../../../features/shared/custom-components/custom-pagination/modals/enum/custom-pagination.enum'
import { CreditNote } from '../../../models/creditnote.model'
import { BaseCountry } from '../../../models/enum/country.enum'
import { SharedKey, SharedValue } from '../../../models/enum/shared.enum'
import { IHistoryList } from '../../../models/interface/history-list.interface'
import { ISharedPagination } from '../../../models/interface/shared.interface'
import { ShipmentCreditClaim } from '../../../models/shipment-credit-claim'
import { AppConfigurationService } from '../../app-configuration.service'
import { DocumentService } from '../../document.service'
import { SharedService } from '../../shared/shared.service'
pdfMake.vfs = pdfFonts.pdfMake.vfs

@Injectable({
  providedIn: 'root'
})
export class ShipmentCreditClaimService {
  constructor(
    private appConfigurationService: AppConfigurationService,
    private http: HttpClient,
    private sharedService: SharedService,
    private documentService: DocumentService,
    protected userIdService: UserIdService
  ) {}

  public getShipmentCreditClaim(params?: ISharedPagination): Observable<ShipmentCreditClaim> {
    let payload: HttpParams = new HttpParams().set(SharedKey.pageSize, SharedValue.pageSize.toString())

    if (params?.pageNumber) {
      payload = payload.set(SharedKey.currentPage, (params.pageNumber - 1).toString())
    }
    if (params?.sort) {
      payload = payload.set(SharedKey.sort, params.sort)
    }
    if (params?.pageSize) {
      payload = payload.set(SharedKey.pageSize, params.pageSize.toString())
    }

    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/shipmentsCredit`
    return this.http.get<any>(url, { params: payload }).pipe(
      map((res) => {
        res.results = res.results.map((item) => {
          return this.processItem(item)
        })

        res.pagination.type = CustomPaginationType.CUSTOM
        return res
      }),
      retry(1),
      catchError(() => {
        throw new Error(SharedValue.errorTryAgain)
      })
    )
  }

  public getShipmentCreditClaimById(id: string): Observable<any> {
    const payload: HttpParams = new HttpParams().set(SharedKey.pageSize, SharedValue.pageSize.toString())

    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/shipmentsCredit/${id}`
    return this.http.get<any>(url, { params: payload }).pipe(
      map((res) => {
        return this.processItem(res)
      }),
      retry(1),
      catchError(this.sharedService.handleError)
    )
  }
  public getShipmentCreditClaimByAgreementId(code: string): Observable<any> {
    const payload: HttpParams = new HttpParams().set(SharedKey.pageSize, SharedValue.pageSize.toString())

    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/shipmentsCredit/agreement/${code}`
    return this.http.get<any>(url, { params: payload }).pipe(
      map((res) => {
        res.results = res.results.map((item) => {
          return this.processItem(item)
        })

        res.pagination.type = CustomPaginationType.CUSTOM
        return res
      }),
      retry(1),
      catchError(this.sharedService.handleError)
    )
  }
  public getShipmentCreditClaimByMemoId(code: string): Observable<any> {
    const payload: HttpParams = new HttpParams().set(SharedKey.pageSize, SharedValue.pageSize.toString())

    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/shipmentsCredit/search/${code}`
    return this.http.get<any>(url, { params: payload }).pipe(
      map((res) => {
        return this.processItem(res)
      }),
      retry(1),
      catchError(this.sharedService.handleError)
    )
  }
  public downloadShipmentCSV(): Observable<any> {
    const payload: HttpParams = new HttpParams().set(SharedKey.pageSize, '200')

    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/creditClaims`
    return this.http.get<any>(url, { params: payload }).pipe(
      map((res) => {
        return this.processItem(res)
      }),
      retry(1),
      catchError(this.sharedService.handleError)
    )
  }
  public getEntries(id: string, params?: ISharedPagination): Observable<IHistoryList> {
    let payload: HttpParams = new HttpParams().set(SharedKey.pageSize, SharedValue.pageSize.toString())

    if (params?.pageNumber) {
      payload = payload.set(SharedKey.currentPage, (params.pageNumber - 1).toString())
    }
    if (params?.status) {
      payload = payload.set(SharedKey.status, params.status)
    }
    if (params?.shipmentId) {
      payload = payload.set(SharedKey.shipmentId, params.shipmentId)
    }

    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/shipmentsCredit/${id}/shipments`
    return this.http.get<any>(url, { params: payload }).pipe(
      map((res) => {
        if (res.results?.length) {
          res.results = res.results.map((item) => {
            if (item.orderDocumentsNumber?.length) {
              item.orderDocument = item.orderDocumentsNumber[0].documentNumber
            }
            return item
          })
        }
        res.pagination.type = CustomPaginationType.CUSTOM
        return res
      }),
      retry(1),
      catchError(() => {
        return of(null)
      })
    )
  }

  private processItem(item): any {
    if (item.entries?.length) {
      const creditRequestMethod: string = item.entries[0].creditRequestMethod
      item.creditRequestMethod = this.getCreditRequestMethod(creditRequestMethod)
    }
    item.status = this.getStatus(item.value)
    return item
  }

  private getCreditRequestMethod(str: string): string {
    if (!str) {
      return str
    }
    return str
      .split('_')
      .map((s) => {
        return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
      })
      .join(' ')
  }

  private getStatus(value: number): string {
    return value > 0 ? 'Completed' : 'Pending'
  }

  public generateCreditNote(orderDocument): void {
    const docService = this.documentService
    const { customerService, abnOrGstLabel, abnOrGstValue, companyName, paymentTerms } = getCountryBasedInfo(
      this.sharedService.baseSiteId
    )

    const creditNote: CreditNote = {
      ...mapOrderDocumentCommonFields(orderDocument),
      billingReference: orderDocument.billingReference,
      description: orderDocument.creditNoteDescriptions,
      subtotal: (+orderDocument.subTotal)?.toFixed(2) || 0.0,
      documentType: orderDocument.documentType?.code
    }
    generatePdfCreditNote(creditNote)

    function mapOrderDocumentCommonFields(orderDocument: any): any {
      return {
        partnerShipToAddress: orderDocument.partnerShipToAddress,
        partnerBillToAddress: orderDocument.partnerbBillToAddress,
        invoiceDocumentNumber: orderDocument.documentNumber,
        invoiceDocumentDate: orderDocument.invoiceDate,
        payerNumber: orderDocument.payerNumber,
        deliveryDocumentNumber: orderDocument.deliveryNumber,
        deliveryDate: orderDocument.deliveryDate,
        salesOrder: orderDocument.orderNumber,
        customerOrderNumber: orderDocument.customerOrderNumber,
        paymentTerms: orderDocument.termsOfPayment,
        incoTerms: orderDocument.incoTerms1,
        discountAvailable: (+orderDocument.finalDiscount).toFixed(2),
        gstTotal: orderDocument.invoiceItemsData?.overAllTax?.value?.toFixed(2) || 0.0,
        currency: orderDocument.currency?.isocode,
        total: orderDocument.invoiceItemsData?.grandTotal?.value?.toFixed(2) || 0.0,
        subTotal: orderDocument.invoiceItemsData?.netValue?.value?.toFixed(2) || 0.0,
        items: orderDocument.invoiceItemsData?.entries.map((invoiceItemData) => {
          const item: any = {
            itemNumber: invoiceItemData.posNo || '',
            marketCode: invoiceItemData.marketCode || '',
            materialNumber: invoiceItemData.product?.code || '',
            description: invoiceItemData.product?.name || '',
            Quantity: invoiceItemData.quantity || '',
            uom: invoiceItemData.unit || '',
            price: invoiceItemData.grossPrice?.value?.toFixed(2) || 0.0,
            taxRate: invoiceItemData.gst?.toFixed(2) || 0.0,
            itemValue: invoiceItemData.netPrice?.value?.toFixed(2) || 0.0
          }
          return item
        })
      }
    }

    function generatePdfCreditNote(creditNote: any): void {
      const type = ' CR'
      const documentDefinition = {
        footer: function (currentPage, pageCount) {
          return [
            {
              text: '\nPage                     ' + currentPage.toString() + ' of ' + pageCount,
              alignment: 'center'
            }
          ]
        },
        content: [
          {
            columns: [
              {
                image: docService.getAsaleoLogo(),
                alignment: 'left',
                width: 60
              },
              {
                text: '',
                width: '*'
              },
              {
                text: [
                  {
                    text: 'Customer Service\n',
                    fontSize: 5,
                    alignment: 'left'
                  },
                  {
                    text: customerService,
                    fontSize: 5,
                    alignment: 'left'
                  },
                  {
                    text: 'Adjustment Note',
                    fontSize: 18,
                    alignment: 'center'
                  }
                ],
                alignment: 'center',
                width: 150
              }
            ]
          },
          { text: '\n' },
          {
            columnGap: 10,
            columns: [
              {
                table: {
                  widths: ['*'],
                  body: [
                    [
                      {
                        stack: [
                          { text: 'Bill To', style: 'defaultFontBold' },
                          { text: ' ', style: 'defaultFont' },
                          {
                            style: 'defaultFont',
                            text: creditNote.partnerBillToAddress?.name
                          },
                          {
                            style: 'defaultFont',
                            text: creditNote.partnerBillToAddress?.partnerCode
                          },
                          {
                            style: 'defaultFont',
                            text: creditNote.partnerBillToAddress?.streetHouseNumber1
                          },
                          {
                            style: 'defaultFont',
                            text:
                              creditNote.partnerBillToAddress?.city + ' ' + creditNote.partnerBillToAddress?.postalCode
                          },
                          {
                            style: 'defaultFont',
                            text: creditNote.partnerBillToAddress?.countryCode
                          }
                        ]
                      }
                    ]
                  ]
                }
              },
              {
                table: {
                  widths: ['*'],
                  body: [
                    [
                      {
                        stack: [
                          { text: 'Ship To', style: 'defaultFontBold' },
                          { text: ' ', style: 'defaultFont' },
                          {
                            style: 'defaultFont',
                            text: creditNote.partnerShipToAddress?.name
                          },
                          {
                            style: 'defaultFont',
                            text: creditNote.partnerShipToAddress?.partnerCode
                          },
                          {
                            style: 'defaultFont',
                            text: creditNote.partnerShipToAddress?.streetHouseNumber1
                          },
                          {
                            style: 'defaultFont',
                            text:
                              creditNote.partnerShipToAddress?.city + ' ' + creditNote.partnerShipToAddress?.postalCode
                          },
                          {
                            style: 'defaultFont',
                            text: creditNote.partnerShipToAddress?.countryCode
                          }
                        ]
                      }
                    ]
                  ]
                }
              },
              {
                text: ' ',
                width: 25
              },
              {
                width: '*',
                columns: [
                  {
                    stack: [
                      {
                        text: 'Credit Doc. Number',
                        style: 'defaultFontBold',
                        lineHeight: 1.2
                      },
                      {
                        text: 'Credit Doc. Date',
                        style: 'defaultFontBold',
                        lineHeight: 1.2
                      },
                      {
                        text: 'Billing Reference',
                        style: 'defaultFontBold',
                        lineHeight: 1.2
                      },
                      {
                        text: 'Payer Number',
                        style: 'defaultFontBold',
                        lineHeight: 1.2
                      }
                    ]
                  },
                  {
                    stack: [
                      {
                        text: creditNote.invoiceDocumentNumber,
                        style: 'defaultFont',
                        lineHeight: 1.2
                      },
                      {
                        text: creditNote.invoiceDocumentDate,
                        style: 'defaultFont',
                        lineHeight: 1.2
                      },
                      {
                        text: creditNote.billingReference,
                        style: 'defaultFont',
                        lineHeight: 1.2
                      },
                      {
                        text: creditNote.payerNumber,
                        style: 'defaultFont',
                        lineHeight: 1.2
                      }
                    ]
                  }
                ]
              }
            ]
          },
          { text: '\n\n' },
          {
            table: {
              widths: ['*'],
              body: [
                [
                  {
                    layout: 'noBorders',
                    table: {
                      headerRows: 1,
                      widths: [40, 40, 40, '*', 40, 40, 40, 40, 40],
                      body: [
                        [
                          { style: 'defaultFontBold', text: 'Item' },
                          { style: 'defaultFontBold', text: 'Market code' },
                          { style: 'defaultFontBold', text: 'Material' },
                          { style: 'defaultFontBold', text: 'Description' },
                          { style: 'defaultFontBold', text: 'Quantity' },
                          { style: 'defaultFontBold', text: 'UOM' },
                          { style: 'defaultFontBold', text: 'Price' },
                          { style: 'defaultFontBold', text: 'GST%' },
                          { style: 'defaultFontBold', text: 'Value' }
                        ]
                      ]
                    }
                  }
                ],
                [
                  {
                    layout: 'noBorders',
                    table: {
                      widths: ['*'],
                      body: [
                        [
                          {
                            columns: [
                              {
                                text: ' ',
                                width: 30
                              },
                              {
                                text: 'Sales Order: ' + creditNote.salesOrder,
                                style: 'defaultFontBold',
                                width: 150
                              },
                              {
                                text: 'Cust.Order Number: ' + creditNote.customerOrderNumber + '\n',
                                style: 'defaultFontBold',
                                width: 150
                              }
                            ]
                          }
                        ],
                        [
                          {
                            layout: 'noBorders',
                            table: {
                              widths: [40, 40, 40, '*', 40, 40, 40, 40, 40],
                              body: []
                            }
                          }
                        ],
                        [{ text: '\n' }],
                        [
                          {
                            stack: [
                              {
                                style: 'defaultFontBold',
                                columns: [
                                  {
                                    text: '',
                                    width: '*'
                                  },
                                  {
                                    text: 'Subtotal',
                                    width: 50
                                  },
                                  {
                                    text: ' ',
                                    width: 50
                                  },
                                  {
                                    text: creditNote.subTotal + type,
                                    width: 50,
                                    alignment: 'right'
                                  },
                                  {
                                    text: ' ',
                                    width: 25
                                  }
                                ]
                              },
                              {
                                text: paymentTerms,
                                style: 'small'
                              }
                            ]
                          }
                        ],
                        [
                          {
                            style: 'defaultFontBold',
                            columns: [
                              {
                                text: '',
                                width: '*'
                              },
                              {
                                text: 'GST\nTotal',
                                width: 50
                              },
                              {
                                text: '\n' + creditNote.currency,
                                width: 50,
                                alignment: 'center'
                              },
                              {
                                text: creditNote.gstTotal + type + '\n' + creditNote.total + type,
                                width: 50,
                                alignment: 'right'
                              },
                              {
                                text: ' ',
                                width: 25
                              }
                            ]
                          }
                        ]
                      ]
                    }
                  }
                ]
              ]
            }
          },
          {
            columns: [
              {
                stack: [
                  {
                    text: [
                      {
                        text: `\n${abnOrGstLabel}: `,
                        style: 'defaultFontBold'
                      },
                      { text: abnOrGstValue, style: 'defaultFontBold' }
                    ]
                  },
                  {
                    text: companyName,
                    style: 'defaultFont'
                  }
                ]
              },
              {
                text: '\nCREDIT',
                style: 'defaultFontBold',
                alignment: 'left'
              },
              { text: ' ' }
            ]
          },
          {
            text: docService.footerInfo,
            style: 'defaultFontBold'
          }
        ],
        styles: {
          header: {
            fontSize: 18,
            bold: true
          },
          table: {
            fontSize: 7,
            bold: true
          },
          tableContent: {
            fontSize: 6,
            bold: false
          },
          bigger: {
            fontSize: 15,
            italics: true
          },
          small: {
            fontSize: 5
          },
          SmallBold: {
            fontSize: 6,
            bold: true
          },
          defaultFont: {
            fontSize: 6
          },
          defaultFontBold: {
            fontSize: 6,
            bold: true
          }
        },
        defaultStyle: {
          columnGap: 0
        },
        pageMargins: [40, 40, 40, 48.5]
      }

      const innerTable: any = documentDefinition.content[4].table.body[1][0]
      const entries: any = innerTable.table.body[1][0]
      creditNote.items.forEach((item) => {
        const creditNoteItem: any = [
          { style: 'defaultFont', text: item.itemNumber },
          { style: 'defaultFont', text: item.marketCode },
          { style: 'defaultFont', text: item.materialNumber },
          { style: 'defaultFont', text: item.description },
          { style: 'defaultFont', text: item.Quantity?.toString() },
          { style: 'defaultFont', text: item.uom },
          { style: 'defaultFont', text: item.price?.toString() },
          { style: 'defaultFont', text: item.taxRate?.toString() },
          { style: 'defaultFont', text: item.itemValue?.toString() + type }
        ]
        entries.table.body.push(creditNoteItem)
      })
      pdfMake.createPdf(documentDefinition).download('Dispenser Credit Note')
    }

    function getCountryBasedInfo(baseSiteId): any {
      let customerService = docService.customerService
      let abnOrGstLabel = 'ABN'
      let abnOrGstValue = docService.abnNumber
      const companyName = docService.companyName
      const paymentTerms = docService.paymentTerms

      if (baseSiteId == BaseCountry.NZ) {
        customerService = docService.customerService_NZ
        abnOrGstLabel = 'GST Reg number'
        abnOrGstValue = docService.gstNumber
      }

      return {
        customerService,
        abnOrGstLabel,
        abnOrGstValue,
        companyName,
        paymentTerms
      }
    }
  }
}
